import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

interface SubCategory {
  name: string;
  isActive: boolean;
}

interface PillarCategory {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
  subCategories?: SubCategory[];
  isActive: boolean;
}

interface PillarsSectionProps {
  pillars: PillarCategory[];
  loading?: boolean;
}

const PILLAR_COLORS = [
  'var(--forest)',
  'var(--indigo)',
  'var(--rose)',
  'var(--marigold-dark)',
  'var(--forest-dark)'
];

const PILLAR_ORDER = ['move', 'breathe', 'heal', 'nourish', 'recover', 'guide'];

const PillarsSection: React.FC<PillarsSectionProps> = ({ pillars, loading = false }) => {
  const navigate = useNavigate();
  const [openPillar, setOpenPillar] = useState<string | null>(null);

  const visiblePillars = useMemo(
    () => pillars
      .filter((pillar) => pillar.isActive)
      .sort((a, b) => {
        const aIndex = PILLAR_ORDER.indexOf(a.name?.toLowerCase?.() || '');
        const bIndex = PILLAR_ORDER.indexOf(b.name?.toLowerCase?.() || '');
        const safeA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const safeB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        return safeA - safeB;
      })
      .slice(0, 6),
    [pillars]
  );

  if (loading) {
    return (
      <section className="py-24" id="pillars" style={{ background: 'var(--paper-2)', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <p>Loading pillars...</p>
      </section>
    );
  }

  if (visiblePillars.length === 0) {
    return null;
  }

  return (
    <section className="py-24" id="pillars" style={{ background: '#FFFFFF', paddingTop: '40px', paddingBottom: '32px' }}>
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-3 sm:mb-4"
        >
          <h3 className="font-bold tracking-tight text-[16px] sm:text-[18px] text-slate-900 leading-tight m-0">
            Every practice on MetaFit, grouped the way you'd actually look for it.
          </h3>
          <p className="text-slate-500 text-[12px] sm:text-[13px] mt-0.5 max-w-xl font-normal">
            Tap a pillar to see the specialities inside it.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-[22px]">
          {visiblePillars.map((p, index) => {
            const activeTags = p.subCategories ? p.subCategories.filter(s => s.isActive).map(s => s.name) : [];
            const color = PILLAR_COLORS[index % PILLAR_COLORS.length];
            
            return (
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                key={p._id} 
                className={`pillar ${openPillar === p._id ? 'open' : ''} bg-white`}
                style={{ background: '#FFFFFF' }}
                onClick={() => setOpenPillar(openPillar === p._id ? null : p._id)}
                onMouseEnter={() => setOpenPillar(p._id)}
                onMouseLeave={() => setOpenPillar((current) => (current === p._id ? null : current))}
                onFocus={() => setOpenPillar(p._id)}
                tabIndex={0}
              >
                <div className="flex items-center justify-between mb-[18px]">
                  <div className="w-[46px] h-[46px] rounded-xl flex items-center justify-center" style={{ background: color, overflow: 'hidden' }}>
                    {p.icon ? (
                      <img src={p.icon} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                        <path d="M12 4a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/><path d="M9 8l3 2 3-2 4 2-1 3-3-1-1 5 2 5h-3l-1.5-4L10 21H7l2-5-1-5-3 1-1-3 4-2Z"/>
                      </svg>
                    )}
                  </div>
                  <span className="font-mono text-xs text-[#3B5750]">{activeTags.length} specialities</span>
                </div>
                <h3 className="font-serif font-semibold tracking-[-0.01em]">{p.name}</h3>
                <p className="text-sm text-[#3B5750] mb-3.5">{p.description || "Explore specialized treatments and practices tailored for your well-being."}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12.5px', fontFamily: "'IBM Plex Mono', monospace", color: 'var(--ink-soft)' }}>See specialities</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pillar/${encodeURIComponent(p.name.toLowerCase())}`);
                    }}
                    style={{
                      border: '1px solid var(--line)',
                      background: '#FFFFFF',
                      color: 'var(--ink)',
                      borderRadius: '999px',
                      padding: '7px 12px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      marginLeft: 'auto',
                      marginRight: '10px'
                    }}
                  >
                    View all
                  </button>
                  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                </div>
                <div className="pillar-tags">
                  {activeTags.map(t => (
                    <span 
                      key={t} 
                      className="text-[12.5px] px-3 py-1.5 rounded-full bg-[#FFFFFF] border border-[#15302B]/[0.08] text-[#3B5750]"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/speciality/${encodeURIComponent(t.toLowerCase())}`);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PillarsSection;
